// The error taxonomy the whole data layer is typed against: the classes, their guards and the Sentry
// context they carry only make sense read together.
/* oxlint-disable max-lines */
import { Code, ConnectError } from '@connectrpc/connect';
import { type ErrorInfo, ErrorInfoSchema } from '@qdrant/qdrant-cloud-public-api/google/rpc/error_details';
import { type SeverityLevel, type Extras, type Primitive } from '@sentry/core';
// The centralized QueryClient error handler is the choke point for API errors, so it captures directly.
// oxlint-disable-next-line no-restricted-imports
import * as Sentry from '@sentry/react';
import { type DefaultError } from '@tanstack/react-query';
import { type AnyRouter, type NotFoundError, isRedirect } from '@tanstack/react-router';
import { StatusCodes } from 'http-status-codes';
import isNetworkError from 'is-network-error';
import { type QdrantErrorCodeMeta } from '@/api/error-codes';
import { buildReturnUrl } from '@/helpers/url';
import { enqueueSnackbar } from '@/hooks/use-qdrant-snackbar';
import { type LogoutSearchSchema } from '@/router/utils';
import { isObject } from '@/utils/utils';

/**
 * Patch ConnectError.from() to leave instances of `PublicApiError` and `AccessControlError` be,
 * without overshadowing them with a new `ConnectError`:
 * @see https://github.com/connectrpc/connect-es/blob/main/packages/connect/src/protocol/run-call.ts#L180
 */
const connectErrorFrom = ConnectError.from.bind(ConnectError);
ConnectError.from = (reason: unknown, code = Code.Unknown) => {
  if (reason instanceof PublicApiError) {
    return reason;
  }
  // AccessControlError is a plain runtime Error (not a ConnectError): it is a client-side guard thrown
  // by the permissions interceptor before any request is sent. Connect runs ConnectError.from() on
  // whatever an interceptor throws, at the top of the call, regardless of interceptor order — so we
  // tunnel the original instance through untouched (the cast is deliberate) to preserve `isNotFound`
  // and the permissions/privileges payload the error boundary needs.
  if (reason instanceof AccessControlError) {
    // oxlint-disable-next-line eslint-js/no-restricted-syntax
    return reason as unknown as ConnectError;
  }
  // Router redirects are also thrown as exceptions, so we want to let them pass through without interference.
  if (isRedirect(reason)) {
    // oxlint-disable-next-line eslint-js/no-restricted-syntax
    return reason as unknown as ConnectError;
  }
  return connectErrorFrom(reason, code);
};

interface INotFoundError extends NotFoundError {
  get isNotFound(): boolean;
  readonly data?: {
    cause: Error;
  };
}

/**
 * Custom wrapper for `ConnectError`. It is always used instead of a `ConnectError` thanks to an interceptor.
 *
 * Important: During error discrimination, never use `instanceof` and instead use the `isPublicApiError()` function below.
 */
export class PublicApiError extends ConnectError implements INotFoundError {
  static displayName = 'PublicApiError';
  override readonly name = 'PublicApiError';
  readonly data: INotFoundError['data'];

  constructor(...args: ConstructorParameters<typeof ConnectError>) {
    super(...args);
    if (this.isNotFound) {
      this.data = { cause: this };
    }
  }

  static override from(reason: unknown, code = Code.Unknown): PublicApiError {
    if (reason instanceof ConnectError) {
      type OutgoingDetails = ConstructorParameters<typeof ConnectError>[3];
      return new PublicApiError(
        reason.rawMessage,
        reason.code,
        reason.metadata,
        // `details` comes back off the wire already decoded, and goes out as the constructor's input shape.
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion, eslint-js/no-restricted-syntax
        reason.details as unknown as OutgoingDetails,
        reason,
      );
    }
    if (reason instanceof Error) {
      if (reason.name === 'AbortError') {
        // Fetch requests can only be canceled with an AbortController.
        // We detect that condition by looking at the name of the raised
        // error object, and translate to the appropriate status code.
        return new PublicApiError(reason.message, Code.Canceled);
      }
      return new PublicApiError(reason.message, code, undefined, undefined, reason);
    }
    return new PublicApiError(String(reason), code, undefined, undefined, reason);
  }

  static async fromAsync(reason: unknown, code = Code.Unknown): Promise<PublicApiError> {
    if (reason instanceof Response) {
      let body: string;

      try {
        body = await reason.clone().text();
      } catch {
        body = reason.statusText;
      }

      const contentType = reason.headers.get('content-type') ?? 'unknown';
      throw PublicApiError.from(
        new Error(`HTTP ${reason.status} ${reason.statusText} [${contentType}] ${reason.url}: ${body}`),
        code,
      );
    }

    return PublicApiError.from(reason, code);
  }

  // oxlint-disable-next-line eslint-js/no-restricted-syntax
  static override [Symbol.hasInstance](v: unknown): boolean {
    if (!(v instanceof Error)) {
      return false;
    }
    return Object.getPrototypeOf(v) === PublicApiError.prototype;
  }

  get isNotFound(): boolean {
    return this.code === Code.NotFound || this.code === Code.PermissionDenied;
  }

  get detail(): ErrorInfo | void {
    return this.findDetails(ErrorInfoSchema).at(0);
  }

  get description(): string {
    return this.qdrantError?.description ?? this.rawMessage;
  }

  get qdrantError(): QdrantErrorCodeMeta | undefined {
    return this.metadata.get('qc-error-code')
      ? {
          code: (this.metadata.get('qc-error-code') as never) ?? '',
          description: (this.metadata.get('qc-error-description') as never) ?? '',
        }
      : undefined;
  }

  // get qdrantError(): { code: string; description: string } | void {
  //   if (this.detail) {
  //     return {
  //       code: this.detail.metadata['qc-error-code'],
  //       description: this.detail.metadata['qc-error-description'],
  //     };
  //   }
  // }

  get traceId() {
    return this.metadata.get('qc-trace-id') ?? this.metadata.get('trailer-qc-trace-id');
  }

  get status(): StatusCodes {
    return this.statusMap.get(this.code) ?? StatusCodes.INTERNAL_SERVER_ERROR;
  }

  /**
   * User Friendly Messages for specific gRPC error codes.
   * @param code gRPC error code
   * @param rawMessage Original error message
   * @returns User friendly error message
   */

  get userMessage(): string {
    switch (this.code) {
      case Code.InvalidArgument:
        if (this.rawMessage.toLowerCase().includes('validation')) {
          return 'Please check your input and try again.';
        }
        return this.rawMessage;

      case Code.NotFound:
        return 'The requested resource could not be found.';

      case Code.AlreadyExists:
        return 'This resource already exists. Please choose a different name or identifier.';

      case Code.PermissionDenied:
        return 'You do not have permission to perform this action.';

      case Code.Unauthenticated:
        return 'Please sign in to continue.';

      case Code.ResourceExhausted: {
        // The reason can reach us either as the status message or as the `qc-error-description`
        // trailer (e.g. the cluster rate-limit case, E1130), and `description` resolves to an empty
        // string when a `qc-error-code` arrives without a description — so match against both.
        const reason = `${this.rawMessage} ${this.description}`.toLowerCase();
        if (reason.includes('quota')) {
          return 'You have reached your usage limit. Please upgrade your plan or try again later.';
        }
        if (reason.includes('rate')) {
          return 'Too many requests. Please wait a moment and try again.';
        }
        return 'Service temporarily unavailable due to high demand. Please try again later.';
      }

      case Code.FailedPrecondition:
        return 'This action cannot be completed due to the current state. Please check the requirements.';

      case Code.Aborted:
        return 'The operation was cancelled due to a conflict. Please try again.';

      case Code.OutOfRange:
        return 'The provided value is outside the acceptable range.';

      case Code.Unimplemented:
        return 'This feature is not currently available.';

      case Code.Internal:
        return 'An internal error occurred. Please try again or contact support if the problem persists.';

      case Code.Unavailable:
        return 'The service is temporarily unavailable. Please try again in a few moments.';

      case Code.DeadlineExceeded:
        return 'The request took too long to complete. Please try again.';

      case Code.Canceled:
        return 'The operation was cancelled. Please try again if needed.';

      case Code.Unknown:
      default:
        return 'An unexpected error occurred. Please try again or contact support if the problem persists.';
    }
  }

  /**
   * Maps gRPC error codes to HTTP status codes.
   * Based on the standard gRPC to HTTP status code mapping.
   * @see https://grpc.github.io/grpc/core/md_doc_statuscodes.html
   */
  private get statusMap(): ReadonlyMap<Code, StatusCodes> {
    return new Map([
      [Code.Canceled, StatusCodes.REQUEST_TIMEOUT],
      [Code.Unknown, StatusCodes.INTERNAL_SERVER_ERROR],
      [Code.InvalidArgument, StatusCodes.BAD_REQUEST],
      [Code.DeadlineExceeded, StatusCodes.REQUEST_TIMEOUT],
      [Code.NotFound, StatusCodes.NOT_FOUND],
      [Code.AlreadyExists, StatusCodes.CONFLICT],
      [Code.PermissionDenied, StatusCodes.FORBIDDEN],
      [Code.ResourceExhausted, StatusCodes.TOO_MANY_REQUESTS],
      [Code.FailedPrecondition, StatusCodes.PRECONDITION_FAILED],
      [Code.Aborted, StatusCodes.CONFLICT],
      [Code.OutOfRange, StatusCodes.BAD_REQUEST],
      [Code.Unimplemented, StatusCodes.NOT_IMPLEMENTED],
      [Code.Internal, StatusCodes.INTERNAL_SERVER_ERROR],
      [Code.Unavailable, StatusCodes.SERVICE_UNAVAILABLE],
      [Code.DataLoss, StatusCodes.INTERNAL_SERVER_ERROR],
      [Code.Unauthenticated, StatusCodes.UNAUTHORIZED],
    ]);
  }

  override toString(): string {
    return `${Code[this.code]}: ${this.userMessage}`;
  }
}

export function isPublicApiError(obj: unknown): obj is PublicApiError {
  return obj != null && obj instanceof PublicApiError;
}

export function getPublicApiReadableInfo(e: unknown): [string, string?] {
  let message = 'An unexpected error occurred. Please try again or contact support if the problem persists.';
  let traceId;
  if (e instanceof PublicApiError) {
    message = e.userMessage;
    traceId = e.traceId ?? undefined;
  }
  return [message, traceId];
}

function isErrorWithMessage(obj: unknown): obj is { message: string } {
  return isObject(obj) && 'message' in obj && typeof obj.message === 'string';
}

export function hasErrorCause(obj: unknown): obj is { cause: Error } {
  return isObject(obj) && 'cause' in obj && obj.cause instanceof Error;
}

/**
 * Heuristically detect dynamic import "module not found" errors
 * across major browsers for lazy route component handling.
 * @see https://github.com/TanStack/router/blob/1acc8eaf3a07cbe1c19ffcf2d4dcecdef34ffc1f/packages/router-core/src/utils.ts#L476
 */
export function isModuleNotFoundError(error: unknown): error is Error {
  // chrome: "Failed to fetch dynamically imported module: http://localhost:5173/src/routes/posts.index.tsx?tsr-split"
  // firefox: "error loading dynamically imported module: http://localhost:5173/src/routes/posts.index.tsx?tsr-split"
  // safari: "Importing a module script failed."
  if (!isErrorWithMessage(error)) {
    return false;
  }
  return (
    error.message.startsWith('Failed to fetch dynamically imported module') ||
    error.message.startsWith('error loading dynamically imported module') ||
    error.message.startsWith('Importing a module script failed')
  );
}

/**
 * Feature area that owns an error, used for ownership routing and team-level dashboards in Sentry.
 *
 * The first group mirrors the proto service packages (`qdrant.cloud.<domain>.v1`) so that
 * {@link deriveSentryContext} can derive `domain` automatically — keep it in sync with `src/api/services`.
 * The second group covers areas with no backing service that are only ever tagged from manual reports.
 */
export type SentryDomain =
  // Service-derived (auto-tagged from the Connect query key):
  | 'account'
  | 'aggregation'
  | 'auth'
  | 'backups'
  | 'billing'
  | 'booking'
  | 'cluster'
  | 'database'
  | 'hybrid-cloud'
  | 'iam'
  | 'metering'
  | 'monitoring'
  | 'payment'
  | 'platform'
  | 'quota'
  | 'settings'
  // Manual-only (no backing service):
  | 'analytics'
  | 'flags'
  | 'infra';

/**
 * Whether — and how — the user actually saw an error. Lets alerts focus on user-visible failures first.
 */
export type SentryVisibility = 'error-page' | 'error-box' | 'snackbar' | 'redirect-logout' | 'silent';

/**
 * The canonical Sentry tag taxonomy. Known keys are typed so manual reports stay consistent and
 * dashboard-filterable; the open index signature keeps room for the keys derived automatically at
 * the QueryClient choke point (`rpc.service`, `grpc.code`, `surface`, …), see {@link deriveSentryContext}.
 */
export type SentryTags = Record<string, Primitive> & {
  domain?: SentryDomain;
  visibility?: SentryVisibility;
};

export type SentryContext = {
  tags?: SentryTags;
  extra?: Extras;
  level?: SeverityLevel; // Use Sentry's official type
  ignore?: boolean; // Custom flag to indicate that this error should be ignored in Sentry
};

type ErrorWithSentryContext<T extends Error> = T & {
  sentryContext?: SentryContext;
};

export function withSentryContext<T extends Error>(
  cause: T,
  context: SentryContext = {},
  filter: (error: unknown) => boolean = () => true,
): ErrorWithSentryContext<T> {
  // A thrown value isn't guaranteed to be an object (e.g. `throw undefined`/a primitive): there's
  // nothing to attach context to, so bail out rather than letting `defineProperty` throw.
  if (!isObject(cause)) {
    return cause;
  }

  const sentryContext: SentryContext = {
    tags: context.tags,
    extra: context.extra,
    level: context.level,
    ignore: context.ignore ?? !filter(cause),
  };

  Object.defineProperty(cause, 'sentryContext', {
    value: sentryContext,
    enumerable: false,
    configurable: true,
    writable: true,
  });

  return cause;
}

export function isErrorWithSentryContext(error: unknown): error is ErrorWithSentryContext<Error> {
  return isObject(error) && 'sentryContext' in error;
}

/**
 * Merges additional context into an error's Sentry context, preserving what is already attached
 * (unlike calling {@link withSentryContext} again, which would replace it). Appended tags/extras
 * win on conflict, while already-set `level` and `ignore` values (explicit call-site intent) win
 * over the appended ones. Note that {@link withSentryContext} always resolves `ignore` to a
 * boolean, so an appended `ignore` only takes effect on errors without prior context.
 */
export function appendSentryContext<T extends Error>(error: T, context: SentryContext): ErrorWithSentryContext<T> {
  const existing = isErrorWithSentryContext(error) ? (error.sentryContext ?? {}) : {};
  return withSentryContext(error, {
    extra: { ...existing.extra, ...context.extra },
    ignore: existing.ignore ?? context.ignore,
    level: existing.level ?? context.level,
    tags: { ...existing.tags, ...context.tags },
  });
}

/**
 * Marks an error instance as already reported to Sentry. Called from `beforeSend` on first sight so
 * later captures of the same instance (e.g. the centralized QueryClient handler and a not-found
 * boundary both calling `captureException`) are dropped — see {@link isSentryCaptured}.
 */
export function markSentryCaptured<T extends Error>(error: T): T {
  if (!isObject(error)) {
    return error;
  }

  // A frozen/sealed error would make defineProperty throw; skip it (it just won't be deduplicated).
  if (Object.isExtensible(error)) {
    Object.defineProperty(error, 'sentryCaptured', {
      value: true,
      enumerable: false,
      configurable: true,
      writable: true,
    });
  }
  return error;
}

export function isSentryCaptured(error: unknown): boolean {
  return isObject(error) && 'sentryCaptured' in error && error.sentryCaptured === true;
}

export class BaseFetchError extends Error {
  constructor(message: string) {
    super(message);
    this.name = this.constructor.name;
    Object.setPrototypeOf(this, new.target.prototype); // Restore prototype chain
  }
}

export class ParseError extends BaseFetchError {
  constructor(originalError: Error) {
    super('Failed to parse fetch response.');
    this.cause = originalError;
  }
}

export class FetchError extends BaseFetchError implements INotFoundError {
  public status: StatusCodes;
  public url: string;
  public body: unknown;
  readonly data: INotFoundError['data'];

  // oxlint-disable-next-line eslint-js/no-restricted-syntax
  constructor(status: StatusCodes, statusText: string, url: string, body: unknown) {
    const message = statusText || `HTTP Error ${status}`;
    super(message);
    this.status = status;
    this.url = url;
    this.body = body;
    if (this.isNotFound) {
      this.data = { cause: this };
    }
  }

  get isNotFound(): boolean {
    return this.status === StatusCodes.NOT_FOUND;
  }

  get isRetryable(): boolean {
    return [
      StatusCodes.REQUEST_TIMEOUT,
      StatusCodes.INTERNAL_SERVER_ERROR,
      StatusCodes.BAD_GATEWAY,
      StatusCodes.SERVICE_UNAVAILABLE,
    ].includes(this.status);
  }
}

/**
 * Error occurs when an expected account is missing, e.g. when an account ID is provided but not found in the system.
 * It may yield due to a potential (edge case) data inconsistency, and it is used to provide more specific error messages in that scenario.
 */
export class AccountMissingError extends Error implements INotFoundError {
  readonly isNotFound = true;
  readonly data = { cause: this };
  readonly fallbackAccountId: string | undefined;

  constructor(accountId?: string, fallbackAccountId?: string) {
    super(`Account with ID '${accountId ?? 'unknown'}' is missing.`);
    this.name = this.constructor.name;
    this.fallbackAccountId = fallbackAccountId;
    Object.setPrototypeOf(this, new.target.prototype); // Restore prototype chain
  }
}

/**
 * Error occurs when the user does not have the necessary permissions or privileges to perform a certain action.
 *
 * This is a client-side runtime guard thrown by the permissions interceptor before any request is sent, so it is
 * deliberately a plain `Error` (like {@link AccountMissingError}) rather than a `ConnectError` — no response ever
 * happened, and modeling a development/runtime error as a transport response error does not hold up over time.
 * Connect normalizes whatever an interceptor throws via `ConnectError.from()` at the top of the call; the patched
 * `ConnectError.from` above tunnels this instance through untouched, preserving `isNotFound` and the
 * permissions/privileges payload the error boundary renders.
 * @see https://github.com/connectrpc/connect-es/blob/main/packages/connect/src/protocol/run-call.ts#L180
 */
export class AccessControlError extends Error implements INotFoundError {
  readonly isNotFound = true;
  readonly data = { cause: this };

  constructor(
    readonly permissions?: string[],
    readonly privileges?: string[],
  ) {
    super('Access denied.');
    this.name = this.constructor.name;
    Object.setPrototypeOf(this, new.target.prototype); // Restore prototype chain
  }

  override toString() {
    return (
      'Access denied. Missing permissions: ' +
      `[${this.permissions?.join(', ')}], missing privileges: [${this.privileges?.join(', ')}].`
    );
  }
}

export function isFetchError(error: unknown): error is FetchError {
  return error instanceof FetchError;
}

export function isAccountMissingError(err: unknown): err is AccountMissingError {
  return err instanceof AccountMissingError;
}

export function isAccessControlError(err: unknown): err is AccessControlError {
  return err instanceof AccessControlError;
}

export function isNotFoundError(error: unknown): error is PublicApiError {
  return error instanceof PublicApiError && error.code === Code.NotFound;
}

export function isFailedPreconditionError(error: unknown): error is PublicApiError {
  return error instanceof PublicApiError && error.code === Code.FailedPrecondition;
}

export function isAlreadyExistsError(error: unknown): error is PublicApiError {
  return error instanceof PublicApiError && error.code === Code.AlreadyExists;
}

export function isForbiddenError(error: unknown): error is PublicApiError {
  return error instanceof PublicApiError && error.code === Code.PermissionDenied;
}

export function isUnauthorizedError(error?: unknown): error is PublicApiError {
  return error instanceof PublicApiError && error.code === Code.Unauthenticated;
}

export function isUnavailableError(error?: unknown): error is PublicApiError {
  return error instanceof PublicApiError && error.code === Code.Unavailable;
}

/**
 * The fetch client operation timed out.
 * Use case: {@link https://developer.mozilla.org/en-US/docs/Web/API/AbortSignal/timeout_static}.
 */
export function isSignalTimeoutError(error?: unknown): error is DOMException {
  return error instanceof DOMException && error.name === 'TimeoutError';
}

/**
 * The fetch client operation was aborted manually.
 */
export function isSignalAbortError(error?: unknown): error is DOMException {
  return error instanceof DOMException && error.name === 'AbortError';
}

/**
 * The transport operation (rpc) timed out. The request is aborted after
 * the signal's default timeout or the response errored with a timeout.
 */
export function isTimeoutError(error?: unknown): error is ConnectError {
  if (!(error instanceof Error)) {
    return false;
  }
  return 'code' in error && error.code === Code.DeadlineExceeded;
}

function isUnknownError(error?: unknown): error is ConnectError {
  if (!(error instanceof Error)) {
    return false;
  }
  return 'code' in error && error.code === Code.Unknown;
}

/**
 * Network, Timeout and Cancelation errors are considered re-tryable.
 */
export function isRetryableError(error?: unknown): error is DefaultError {
  return (
    isSignalTimeoutError(error) || // <-- Wretch: aborted request - client timeout
    isNetworkError(error) || // <-- Wretch: network error handling
    (isFetchError(error) && error.isRetryable) || // <-- Custom fetch errors, which may include network errors
    isTimeoutError(error) || // <-- ConnectRPC API: aborted request - client timeout
    (isUnknownError(error) && isNetworkError(error.cause)) // <-- ConnectRPC API: network error handling
  );
}

/**
 * Query retry handler for handling query failures.
 * It determines whether a query should be retried based on the error type and failure count.
 * Will only retry on Unauthorized errors, timeout and, network errors.
 */
export function queryRetryHandler(failureCount: number, error: DefaultError) {
  if (isUnauthorizedError(error) || isRetryableError(error)) {
    return failureCount < 1;
  }
  return false;
}

/**
 * Mutation retry handler for handling mutation failures.
 * It determines whether a mutation should be retried based on the error type and failure count.
 * Will only retry on Unauthorized errors.
 */
export function mutationRetryHandler(failureCount: number, error: DefaultError) {
  if (isUnauthorizedError(error)) {
    return failureCount < 1;
  }
  return false;
}

/**
 * Default error handler for all QueryClient errors.
 * It handles unauthorized errors, permission errors, not-found errors, redirects.
 */
export function queryClientErrorHandler(router: AnyRouter, { ssoLoginHint }: { ssoLoginHint: string | undefined }) {
  return function queryClientOnError(error: DefaultError | undefined): Promise<void> | void {
    if (error === undefined) {
      return; // Seems like retries issue a callback too with potentially empty error.
    }
    /**
     * API unauthorized response errors are treated as a logout event.
     * @see https://developer.mozilla.org/en-US/docs/Web/HTTP/Status/401
     */
    if (isUnauthorizedError(error)) {
      const search: LogoutSearchSchema = { aerr: 'invalid', return_to: buildReturnUrl(router) };
      // Redirect to the logout screen with the error message in the URL.
      if (error.detail?.reason === 'mfa_required') {
        search.aerr = 'mfa_required';
        return router.navigate({ to: '/logout', search });
      }
      if (error.detail?.reason === 'JWT_CONNECTION_MISMATCH') {
        const ssoConnection = error.detail.metadata.ssoConnection;
        if (ssoConnection && ssoLoginHint) {
          markSentryCaptured(error);
          delete search.aerr;
          search.sso_connection = ssoConnection;
          search.sso_login_hint = ssoLoginHint;
        } else {
          Sentry.captureException(
            new Error(`JWT_CONNECTION_MISMATCH: invalid params, received: '${ssoConnection}', '${ssoLoginHint}'.`, {
              cause: error,
            }),
            {
              level: 'fatal',
              tags: { visibility: 'redirect-logout' },
            },
          );
        }
      }
      return router.navigate({ to: '/logout', search });
    }

    /**
     * Check if the error is a redirect object
     * @see https://github.com/TanStack/router/blob/main/packages/react-router-with-query/src/index.tsx#L150
     */
    if (isRedirect(error)) {
      error.options._fromLocation = router.state.location;
      return router.navigate(router.resolveRedirect(error).options);
    }

    if (isForbiddenError(error)) {
      enqueueSnackbar('You have insufficient permissions to perform this action.', {
        variant: 'warning',
        preventDuplicate: true,
        anchorOrigin: { vertical: 'top', horizontal: 'right' },
      });
      // An expected product state (the user lacks a server-side permission), handled via the snackbar
      // above — keep it visible in Sentry trends but below alert level, and record how it surfaced.
      appendSentryContext(error, { level: 'warning', tags: { visibility: 'snackbar' } });
    }

    if (isAccessControlError(error)) {
      // Same reasoning as the 403 above: the client-side permissions guard blocking an action is an
      // expected product state rendered as "Access Denied", not a defect.
      appendSentryContext(error, { level: 'warning' });
    }

    /**
     * Manually report query/mutations errors, exempt error types that have been retried.
     *
     * We do not report these on react-dom/client `RootOptions` (`onCaughtError`, etc.)
     * The reason is: errors can happen post-render as well, so with this, we centralize error reporting.
     *
     * Deduplication across surfaces (e.g. a not-found boundary capturing the same error) is handled
     * centrally in {@link markSentryCaptured}'s consumer — Sentry's `beforeSend` marks each error
     * instance on first sight and drops later captures of it.
     */
    if (!isRetryableError(error)) {
      Sentry.captureException(error);
    }
  };
}
